import { OBSTACLE_SCROLL_SPEED, PF_HH } from '../constants';
import type { LevelBlock } from '../defs';
import { spawnObstacleFromDef } from './obstacleSystem';
import { spawnEnemyAt } from './levelSystem';
import type { World } from '../World';

/* ═══════════════════════════════════════════════════════════════════════════
   Le POSEUR d'obstacles de la campagne.

   Le pendant, côté décor collisionnable, du peuplement d'une vague. Quand un
   bloc de niveau s'active, `levelSystem` l'appelle UNE fois : cette fonction
   lit les `ObstaclePlacement` du bloc et fait naître les astéroïdes et les
   complexes militaires qu'il décrit — au sommet du terrain, chacun à son
   abscisse et à sa hauteur d'entrée.

   ── Pourquoi une seule passe, et pas une timeline ──
   Un obstacle ne pilote pas de trajectoire : une fois posé, il DESCEND au
   défilement global (`obstacleSystem`), rien d'autre. Il n'y a donc rien à
   piloter tick après tick, et donc rien qui justifie une file d'attente comme
   celle des vagues. L'échelonnement dans le temps est obtenu autrement : `at`
   (secondes après l'activation) devient une ordonnée de DÉPART au-dessus du
   cadre. La descente fait le reste — la gravité du monde EST l'horloge de la
   section, ce qui supprime tout un état mutable et son entrée dans l'empreinte.

   ── PURE, et sans aléa ──
   Elle vit dans src/sim/ : aucun `three`, aucun DOM, aucune trigonométrie. Et
   surtout elle ne tire JAMAIS le RNG — ni `spawnObstacleFromDef` ni
   `spawnEnemyAt` ne le touchent. Un obstacle et sa sentinelle naissent à des
   positions ENTIÈREMENT déterministes fournies par le contenu ; consommer l'aléa
   ici décalerait le flux de gameplay et invaliderait les golden replays, pour
   aucun gain (la dispersion verticale des vagues existe parce qu'elles arrivent
   en nuées — un complexe se place à la main).

   ── Le complexe militaire ──
   Un placement qui porte des `sentinels` EST un complexe : on fait d'abord
   naître le porteur (obstacle), puis chaque sentinelle — un ennemi ANCRÉ
   ordinaire — à la position du porteur plus son décalage. On réutilise
   `spawnEnemyAt`, le cœur commun du spawn de vague, pour que la sentinelle
   hérite EXACTEMENT du coefficient de PV du monde traversé : une sentinelle
   exemptée tomberait aussi vite au monde 3 qu'au monde 1, et le complexe cesserait
   d'être une menace au moment précis où il devrait compter le plus.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Fait naître les obstacles (et sentinelles) déclarés par un bloc de niveau.
 *  No-op silencieux si le bloc n'en porte aucun — le cas de l'écrasante
 *  majorité des blocs. */
export function placeObstacles(w: World, block: LevelBlock): void {
  const placements = block.obstacles;
  if (!placements) return;

  for (const pl of placements) {
    // `at` (secondes après l'activation) → ordonnée de départ au-dessus du
    // cadre. L'obstacle franchit le haut du cadre `at` secondes plus tard, en
    // descendant au défilement. `at = 0` = pile en haut du cadre.
    const y = PF_HH + OBSTACLE_SCROLL_SPEED * pl.at;

    const slot = spawnObstacleFromDef(w, pl.obstacle, pl.x, y);
    // Porteur refusé (identifiant inconnu ou pool saturé) : on n'ancre PAS ses
    // sentinelles dans le vide. Un complexe sans porteur n'a pas de sens, et des
    // canons flottants seraient une panne visible ; on saute le placement entier.
    if (slot < 0) continue;

    const sentinels = pl.sentinels;
    if (!sentinels) continue;
    for (const s of sentinels) {
      const idx = w.defs.enemyByI.get(s.enemy);
      // Sentinelle à l'identifiant inconnu : ignorée, le porteur reste posé.
      // Mieux vaut un complexe désarmé qu'un plantage — la validation du registre
      // aura de toute façon refusé le contenu bien avant le jeu.
      if (idx === undefined) continue;
      const rider = spawnEnemyAt(w, idx, pl.x + s.offX, y + s.offY);
      // Chevauchement : la sentinelle est BOULONNÉE au porteur et le suit (une
      // plateforme mobile emporte ses canons). Sinon elle reste une tourelle
      // indépendante et l'obstacle défile sous elle (comportement historique).
      if (s.ride && rider >= 0) {
        w.enemies.ride(rider, slot, s.offX, s.offY, w.obstacles.uid[slot]!);
      }
    }
  }
}
